import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { get, patch, post } from "../lib/api";
import { getRef } from "../lib/referral";
import { trackConversion } from "../lib/ads";
import { useI18n } from "../i18n";

const AuthContext = createContext(null);

// Returning from Google (#session_id=...): exchange it exactly once per page load, before any /auth/me call.
// A module-level promise survives StrictMode double effects and re-renders.
let googleExchange;
export const startGoogleExchange = () => {
  if (googleExchange !== undefined) return googleExchange;
  const sid = new URLSearchParams(window.location.hash.slice(1)).get("session_id");
  if (sid) window.history.replaceState(null, "", window.location.pathname + window.location.search);
  googleExchange = sid ? post("/auth/google", { session_id: sid, ref_code: getRef() }) : null;
  return googleExchange;
};
export const googleReason = (e) => {
  const d = e?.response?.data?.detail;
  return !e?.response ? "network_or_cors" : d?.reason || d?.code || `http_${e.response.status}`;
};
export const _resetGoogleExchange = () => { googleExchange = undefined; };

export const AuthProvider = ({ children }) => {
  const [me, setMe] = useState(null); // null = checking, false = logged out, object = session
  const [googleError, setGoogleError] = useState(null);
  const { lang, setLang } = useI18n();

  const apply = useCallback((data) => {
    setMe(data);
    if (data.user.language) setLang(data.user.language);
    else patch("/auth/me", { language: localStorage.getItem("am_lang") || "en" }).catch(() => {});
    return data;
  }, [setLang]);

  const refreshMe = useCallback(() => get("/auth/me").then(setMe).catch(() => setMe(false)), []);

  useEffect(() => {
    const onOut = () => setMe(false);
    window.addEventListener("am:logged-out", onOut);
    const hasHash = window.location.hash?.includes("session_id=");
    const pending = hasHash || googleExchange !== undefined ? startGoogleExchange() : undefined;
    if (pending) {
      pending.then((d) => { if (d.new_user) trackConversion("signup"); return apply(d); }).catch((e) => {
        const reason = googleReason(e);
        console.warn("google login failed:", reason);
        setGoogleError(reason);
        setMe(false);
      });
    } else if (pending === null) {
      setGoogleError("no_session_id");
      setMe(false);
    } else {
      get("/auth/me").then(apply).catch(() => setMe(false));
    }
    return () => window.removeEventListener("am:logged-out", onOut);
  }, [apply]);

  const value = {
    me, setMe, refreshMe, googleError,
    login: (email, password) => post("/auth/login", { email, password }).then(apply),
    signup: (name, email, password) => post("/auth/register", { name, email, password, language: lang, ref_code: getRef() }).then((d) => { trackConversion("signup"); return apply(d); }),
    logout: () => post("/auth/logout").finally(() => setMe(false)),
    switchWorkspace: (id) => post(`/workspaces/${id}/switch`).then(refreshMe),
    saveLanguage: (code) => { setLang(code); if (me) patch("/auth/me", { language: code }).catch(() => {}); },
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
